/**
 * Deferred translation hook — avoids pulling react-i18next into the critical bundle.
 * Returns fallback strings immediately, then re-renders with real translations once i18n loads.
 * Subscribes to language changes so switching languages triggers re-renders.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

type TFunction = (key: string, fallback?: string) => string;

let i18nInstance: any = null;
let loadingPromise: Promise<void> | null = null;
const readySubscribers = new Set<() => void>();

function loadI18n(): Promise<void> {
  if (!loadingPromise) {
    loadingPromise = import('@/i18n').then(({ default: i18n }) => {
      i18nInstance = i18n;
      readySubscribers.forEach(fn => fn());
      readySubscribers.clear();
    }).catch(() => {});
  }
  return loadingPromise;
}

// Preload i18n after idle
if (typeof window !== 'undefined') {
  const startLoad = () => loadI18n();
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(startLoad, { timeout: 2000 });
  } else {
    setTimeout(startLoad, 200);
  }
}

export function useDeferredTranslation(): { t: TFunction } {
  const [, setTick] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const rerender = () => {
      if (mounted.current) setTick(n => n + 1);
    };

    if (!i18nInstance) {
      // Wait for i18n to load
      readySubscribers.add(rerender);
      loadI18n();
    }

    // Subscribe to language changes once i18n is ready
    let unsubscribe: (() => void) | null = null;
    const setupListener = () => {
      if (i18nInstance) {
        i18nInstance.on('languageChanged', rerender);
        unsubscribe = () => i18nInstance.off('languageChanged', rerender);
        // Trigger initial render with real translations
        rerender();
      }
    };

    if (i18nInstance) {
      setupListener();
    } else {
      // Setup listener once i18n loads
      const origRerender = rerender;
      const wrappedRerender = () => {
        origRerender();
        setupListener();
      };
      readySubscribers.delete(rerender);
      readySubscribers.add(wrappedRerender);
    }

    return () => {
      mounted.current = false;
      readySubscribers.delete(rerender);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const t: TFunction = useCallback((key: string, fallback?: string) => {
    if (i18nInstance) {
      const result = i18nInstance.t(key);
      return (result === key && fallback) ? fallback : result;
    }
    return fallback || key.split('.').pop() || key;
  }, []);

  return { t };
}
